
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import template from './avatar.twig';
import demoImageSmall from './demo/tyler-64.png';
import demoImageMedium from './demo/tyler-335.png';
import demoImageLarge from './demo/tyler-768.png';
const demoImageSrcset = [
  `${demoImageSmall} 64w`,
  `${demoImageMedium} 335w`,
  `${demoImageLarge} 768w`,
].join(', ');
const avatarStory = (args) => {
  // Don't bother with the size option if it is the default
  if (args.size === 'medium') {
    delete args.size;
  }
  return template(args);
};


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Components/Avatar" args={{
      size: 'medium'
    }} argTypes={{
      size: {
        options: ['medium', 'full'],
        type: {
          name: 'string'
        },
        control: {
          type: 'select'
        }
      },
      src: {
        type: {
          name: 'string'
        }
      },
      srcset: {
        type: {
          name: 'string'
        }
      },
      sizes: {
        type: {
          name: 'string'
        }
      },
      alt: {
        type: {
          name: 'string'
        }
      },
      width: {
        type: {
          name: 'number'
        }
      },
      height: {
        type: {
          name: 'number'
        }
      }
    }} mdxType="Meta" />
    <h1>{`Avatar`}</h1>
    <p>{`Displays a user, organization or product `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Avatar_(computing)"
      }}>{`avatar`}</a>{` or profile picture.`}</p>
    <p>{`The size is fixed to the value of `}<a parentName="p" {...{
        "href": "/docs/design-tokens-size--page#sizing"
      }}>{`our `}<inlineCode parentName="a">{`sizes.$avatar-medium`}</inlineCode>{` token by default`}</a>{`, making this an ideal pattern to use within `}<a parentName="p" {...{
        "href": "/docs/objects-media--image"
      }}>{`a Media object`}</a>{`. If the pattern contains no image, it will show a plain color.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Empty" mdxType="Story">{avatarStory.bind({})}</Story>
  <Story name="With Image" args={{
        src: demoImageSmall,
        srcset: demoImageSrcset,
        sizes: '60px'
      }} mdxType="Story">
    {avatarStory.bind({})}
  </Story>
    </Canvas>
    <h2>{`Small`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`c-avatar--small`}</inlineCode>{` modifier sizes the avatar down to the value of our `}<inlineCode parentName="p">{`sizes.$avatar-small`}</inlineCode>{` token. This may be useful when accompanying a single line of text or showing many avatars in a condensed space.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Small (Empty)" args={{
        size: 'small'
      }} mdxType="Story">
    {avatarStory.bind({})}
  </Story>
  <Story name="Small (With Image)" args={{
        size: 'small',
        src: demoImageSmall,
        srcset: demoImageSrcset,
        sizes: '38px'
      }} mdxType="Story">
    {avatarStory.bind({})}
  </Story>
    </Canvas>
    <h2>{`Full Width`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`c-avatar--full`}</inlineCode>{` modifier sizes the component to `}<inlineCode parentName="p">{`100%`}</inlineCode>{` of its container width.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Full Width" args={{
        src: demoImageSmall,
        srcset: demoImageSrcset,
        size: 'full',
        sizes: '100vw'
      }} mdxType="Story">
    {avatarStory.bind({})}
  </Story>
    </Canvas>
    <h2>{`Template Properties`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`alt`}</inlineCode>{`: Value for the inner `}<inlineCode parentName="li">{`img`}</inlineCode>{` element's `}<inlineCode parentName="li">{`alt`}</inlineCode>{` attribute.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`class`}</inlineCode>{`: Append a class to the `}<inlineCode parentName="li">{`c-avatar`}</inlineCode>{` element.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`content`}</inlineCode>{` (block): Directly specify content for the element. Useful for adding a `}<inlineCode parentName="li">{`picture`}</inlineCode>{` or `}<inlineCode parentName="li">{`svg`}</inlineCode>{` element directly.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`height`}</inlineCode>{`: Value for the inner `}<inlineCode parentName="li">{`img`}</inlineCode>{` element's `}<inlineCode parentName="li">{`height`}</inlineCode>{` attribute. Will default to `}<inlineCode parentName="li">{`width`}</inlineCode>{` if specified.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`loading`}</inlineCode>{`: Value for the inner `}<inlineCode parentName="li">{`img`}</inlineCode>{` element's `}<inlineCode parentName="li">{`loading`}</inlineCode>{` attribute. Defaults to `}<inlineCode parentName="li">{`lazy`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`size`}</inlineCode>{`: Shorthand for appending a size modifier class, for example `}<inlineCode parentName="li">{`full`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`sizes`}</inlineCode>{`: Value for the inner `}<inlineCode parentName="li">{`img`}</inlineCode>{` element's `}<inlineCode parentName="li">{`sizes`}</inlineCode>{` attribute.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`src`}</inlineCode>{`: Value for the inner `}<inlineCode parentName="li">{`img`}</inlineCode>{` element's `}<inlineCode parentName="li">{`src`}</inlineCode>{` attribute. If omitted, no `}<inlineCode parentName="li">{`img`}</inlineCode>{` will be output.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`srcset`}</inlineCode>{`: Value for the inner `}<inlineCode parentName="li">{`img`}</inlineCode>{` element's `}<inlineCode parentName="li">{`srcset`}</inlineCode>{` attribute.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`width`}</inlineCode>{`: Value for the inner `}<inlineCode parentName="li">{`img`}</inlineCode>{` element's `}<inlineCode parentName="li">{`width`}</inlineCode>{` attribute. Will default to `}<inlineCode parentName="li">{`height`}</inlineCode>{` if specified.`}</li>
    </ul>
    <h2>{`See also…`}</h2>
    <p>{`To display multiple avatars in a visual cluster, see `}<a parentName="p" {...{
        "href": "/docs/objects-bunch--of-avatars"
      }}>{`the Bunch object`}</a>{`.`}</p>
    <p>{`To display images and other embedded media at a fluid but consistent aspect ratio, see `}<a parentName="p" {...{
        "href": "/docs/objects-embed--image"
      }}>{`the Embed object`}</a>{`.`}</p>
    <p>{`To size and color icons based on surrounding text, see `}<a parentName="p" {...{
        "href": "/docs/components-icon--basic"
      }}>{`the Icon component`}</a>{`.`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const empty = avatarStory.bind({});
empty.storyName = 'Empty';
empty.parameters = { storySource: { source: 'avatarStory.bind({})' } };

export const withImage = avatarStory.bind({});
withImage.storyName = 'With Image';
withImage.args = {
  src: demoImageSmall,
  srcset: demoImageSrcset,
  sizes: '60px'
};
withImage.parameters = { storySource: { source: 'avatarStory.bind({})' } };

export const smallEmpty = avatarStory.bind({});
smallEmpty.storyName = 'Small (Empty)';
smallEmpty.args = {
  size: 'small'
};
smallEmpty.parameters = { storySource: { source: 'avatarStory.bind({})' } };

export const smallWithImage = avatarStory.bind({});
smallWithImage.storyName = 'Small (With Image)';
smallWithImage.args = {
  size: 'small',
  src: demoImageSmall,
  srcset: demoImageSrcset,
  sizes: '38px'
};
smallWithImage.parameters = { storySource: { source: 'avatarStory.bind({})' } };

export const fullWidth = avatarStory.bind({});
fullWidth.storyName = 'Full Width';
fullWidth.args = {
  src: demoImageSmall,
  srcset: demoImageSrcset,
  size: 'full',
  sizes: '100vw'
};
fullWidth.parameters = { storySource: { source: 'avatarStory.bind({})' } };

const componentMeta = { title: 'Components/Avatar', args: {
  size: 'medium'
}, argTypes: {
  size: {
    options: ['medium', 'full'],
    type: {
      name: 'string'
    },
    control: {
      type: 'select'
    }
  },
  src: {
    type: {
      name: 'string'
    }
  },
  srcset: {
    type: {
      name: 'string'
    }
  },
  sizes: {
    type: {
      name: 'string'
    }
  },
  alt: {
    type: {
      name: 'string'
    }
  },
  width: {
    type: {
      name: 'number'
    }
  },
  height: {
    type: {
      name: 'number'
    }
  }
}, includeStories: ["empty","withImage","smallEmpty","smallWithImage","fullWidth"],  };

const mdxStoryNameToKey = {"Empty":"empty","With Image":"withImage","Small (Empty)":"smallEmpty","Small (With Image)":"smallWithImage","Full Width":"fullWidth"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;